import http from "../utils/http";

const baseFields = {
  "fields[0]": "id",
  "fields[1]": "documentId",
  "fields[2]": "posting_number",
  "fields[3]": "market",
  "fields[4]": "state",
  "fields[5]": "in_process_at",
  "fields[6]": "shipment_date",
  "fields[7]": "kiz_status",
  "fields[8]": "return_status",
  "fields[9]": "withdrawal_status",
  "fields[10]": "wbOrderId",
  "fields[11]": "kiz_sent",
  "fields[12]": "article",
};

const itemPopulate = {
  "populate[posting_items][fields][0]": "kiz_code",
  "populate[posting_items][fields][1]": "sku",
  "populate[posting_items][fields][2]": "offer_id",
  "populate[posting_items][fields][3]": "name",
  "populate[posting_items][fields][4]": "quantity",
  "populate[posting_items][fields][5]": "price",
  "populate[posting_items][fields][6]": "documentId",
  "populate[posting_items][fields][7]": "kiz_additional_codes",
  "populate[posting_items][fields][8]": "kiz_returned",
};

const sortByInProcess = (a, b) => {
  const aDate = a.in_process_at ? new Date(a.in_process_at).getTime() : 0;
  const bDate = b.in_process_at ? new Date(b.in_process_at).getTime() : 0;
  return bDate - aDate;
};

function buildParams({ market, state, page, pageSize, since }) {
  const params = {
    ...baseFields,
    ...itemPopulate,
    "filters[market][$eq]": market,
    "filters[state][$eq]": state,
    "pagination[page]": page,
    "pagination[pageSize]": pageSize,
    "sort[0]": "in_process_at:desc",
  };

  if (since) {
    params["filters[in_process_at][$gte]"] = since;
  }

  return params;
}

function extractItems(raw) {
  const attr = raw.attributes || {};
  const directItems = raw.posting_items || attr.posting_items;

  if (Array.isArray(directItems)) return directItems;

  const relationItems = directItems?.data;
  if (Array.isArray(relationItems)) {
    return relationItems.map((it) => ({ id: it.id, ...(it.attributes || {}), ...it }));
  }

  return [];
}

function mapPosting(raw) {
  const attr = raw.attributes || {};
  const items = extractItems(raw);
  const kizCodes = items
    .flatMap((item) => [
      item.kiz_code,
      ...(Array.isArray(item.kiz_additional_codes)
        ? item.kiz_additional_codes
        : []),
    ])
    .filter((code) => typeof code === "string" && code.trim() !== "");

  return {
    id: raw.id,
    documentId: raw.documentId || attr.documentId,
    posting_number: raw.posting_number || attr.posting_number,
    market: raw.market || attr.market,
    state: raw.state || attr.state,
    in_process_at: raw.in_process_at || attr.in_process_at,
    shipment_date: raw.shipment_date || attr.shipment_date,
    kiz_status: raw.kiz_status || attr.kiz_status,
    return_status: raw.return_status || attr.return_status,
    withdrawal_status: raw.withdrawal_status || attr.withdrawal_status,
    wbOrderId: raw.wbOrderId || attr.wbOrderId,
    kiz_sent: raw.kiz_sent ?? attr.kiz_sent,
    article: raw.article || attr.article,
    product_title: raw.product_title || attr.product_title,
    kiz_codes: kizCodes,
    items,
  };
}

export async function fetchPostingsPage({
  market,
  state,
  page,
  pageSize,
  since,
}) {
  const response = await http.get("/postings", {
    params: buildParams({ market, state, page, pageSize, since }),
  });
  const rows = (response.data?.data || []).map(mapPosting).sort(sortByInProcess);
  const total = response.data?.meta?.pagination?.total ?? rows.length;
  return { rows, total };
}

export async function fetchAllPostings({
  market,
  state,
  pageSize = 200,
  since,
}) {
  const allRows = [];
  let page = 1;
  let total = null;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { rows, total: pageTotal } = await fetchPostingsPage({
      market,
      state,
      page,
      pageSize,
      since,
    });

    allRows.push(...rows);
    total = pageTotal ?? total ?? 0;

    if (allRows.length >= total || rows.length === 0) {
      break;
    }

    page += 1;
  }

  return allRows;
}
